import type { components } from "@brandex/api-client";
import { formatTaka } from "@brandex/i18n";
import { useBrandex } from "@brandex/dashboard";
import { Alert, Badge, Button, Field, Input, Select, Spinner } from "@brandex/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useIsSuperAdmin } from "../data";
import { fieldError, QueryError } from "../views";
import { currentPeriod } from "./pipeline";

type Invoice = components["schemas"]["InvoiceDto"];
type Method = NonNullable<Invoice["payment"]>["method"];
type RefundKind = Invoice["refunds"][number]["kind"];

/** Refunds above this (paisa) also need a Super admin (ADR 0016 §2). */
export const REFUND_SUPER_ADMIN_ABOVE = 5_000_000;

export const METHODS: readonly { value: Method; label: string }[] = [
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "bkash", label: "bKash" },
  { value: "nagad", label: "Nagad" },
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "other", label: "Other" },
];
const STATUS: Record<Invoice["status"], { label: string; tone: "success" | "warning" | "neutral" }> = {
  due: { label: "Due", tone: "warning" },
  overdue: { label: "Overdue", tone: "warning" },
  paid: { label: "Paid", tone: "success" },
  void: { label: "Void", tone: "neutral" },
};
const key = ["admin", "invoices"] as const;
/** Today in Bangladesh, `YYYY-MM-DD`. */
function dhakaToday(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Dhaka" }).format(new Date());
}

/**
 * Payments (F-18, ADR 0016 §2): bill a month (the worker also does it on
 * the 1st), record payments by hand and refund by the published rules.
 * Totals: billed, collected (less refunds), outstanding, refunded.
 */
export function PaymentsPage() {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [period, setPeriod] = useState(currentPeriod);
  const ledger = useQuery({
    queryKey: [...key, period],
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/admin/invoices", { params: { query: { period } } });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
  const bill = useMutation({
    mutationFn: async () => (await api.http.POST("/v1/admin/invoices/bill", { body: { period } })).data ?? [],
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });
  const money = (paisa: number) => formatTaka("en", paisa);
  return (
    <div className="grid gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Payments</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Billed monthly in advance, due in 7 days. Invoices turn overdue on day 8 and payments staff are alerted at 15 days; production is never paused automatically and the doctor's site stays up.
            Refunds need a reason; above ৳50,000 a Super admin.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <Field label="Month">
            <Input type="month" className="w-40" value={period} onChange={(e) => e.target.value && setPeriod(e.target.value)} />
          </Field>
          <Button loading={bill.isPending} onClick={() => bill.mutate()}>Bill this month</Button>
        </div>
      </header>
      {bill.isSuccess ? <Alert tone="success">{bill.data.length === 0 ? "Already billed: nothing new." : `Created ${bill.data.length} invoice(s).`}</Alert> : null}
      {bill.isError ? <QueryError error={bill.error} /> : null}
      {ledger.isPending ? <Spinner label="Loading…" /> : null}
      {ledger.isError ? <QueryError error={ledger.error} /> : null}
      {ledger.data !== undefined ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["Billed this month", ledger.data.totals.billed_paisa],
              ["Collected", ledger.data.totals.collected_paisa],
              ["Outstanding", ledger.data.totals.outstanding_paisa],
              ["Refunded", ledger.data.totals.refunded_paisa],
            ].map(([label, value]) => (
              <div key={label} className="glass rounded-[var(--radius-lg)] p-5">
                <p className="text-sm text-muted-foreground">{label}</p>
                <p className="mt-1 font-display text-3xl font-bold tabular-nums">{money(Number(value))}</p>
              </div>
            ))}
          </div>
          <div className="glass overflow-x-auto rounded-[var(--radius-lg)]">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr><th className="px-4 py-3">Client</th><th>Amount</th><th>Due</th><th>Status</th><th>Payment</th><th className="px-4" /></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {ledger.data.items.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No invoices for this month. Use “Bill this month”.</td></tr>
                ) : (
                  ledger.data.items.map((i) => <InvoiceRow key={i.id} invoice={i} />)
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}

function InvoiceRow({ invoice: i }: { invoice: Invoice }) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [paying, setPaying] = useState(false);
  const [method, setMethod] = useState<Method>("bkash");
  const [reference, setReference] = useState("");
  const [paidOn, setPaidOn] = useState(dhakaToday);
  const [refunding, setRefunding] = useState(false);
  const path = { params: { path: { invoice_id: i.id } } };
  const done = () => {
    setPaying(false);
    void qc.invalidateQueries({ queryKey: key });
  };
  const paid = useMutation({
    mutationFn: async () => {
      await api.http.POST("/v1/admin/invoices/{invoice_id}/paid", { ...path, body: { method, reference, paid_on: paidOn } });
    },
    onSuccess: done,
  });
  const overdue = useMutation({
    mutationFn: async () => {
      await api.http.POST("/v1/admin/invoices/{invoice_id}/overdue", path);
    },
    onSuccess: done,
  });
  const name = i.doctor_name ?? i.organization_id;
  const open = i.status === "due" || i.status === "overdue";
  return (
    <tr className="align-top">
      <td className="px-4 py-3 font-medium">{name}</td>
      <td className="py-3 tabular-nums">
        {formatTaka("en", i.amount_paisa)}
        {i.months > 1 ? <p className="text-xs text-muted-foreground">{i.months} months to {i.covers_through} · saved {formatTaka("en", i.discount_paisa)}</p> : null}
        {i.refunded_paisa > 0 ? <p className="text-xs text-muted-foreground">Refunded {formatTaka("en", i.refunded_paisa)}</p> : null}
      </td>
      <td className="py-3">
        {i.due_on}
        {i.days_overdue > 0 ? <p className={i.days_overdue >= 15 ? "text-xs font-medium text-destructive" : "text-xs text-muted-foreground"}>{i.days_overdue} days late{i.late_alerted_at ? " · team alerted" : ""}</p> : null}
      </td>
      <td className="py-3"><Badge tone={STATUS[i.status].tone}>{STATUS[i.status].label}</Badge></td>
      <td className="py-3 text-muted-foreground">
        {i.payment ? `${i.payment.paid_on} · ${METHODS.find((m) => m.value === i.payment?.method)?.label ?? ""}${i.payment.reference ? ` · ${i.payment.reference}` : ""}` : i.note || "—"}
        {paying ? (
          <form className="mt-2 flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); paid.mutate(); }}>
            <Select aria-label={`Method for ${name}`} className="w-auto" value={method} onChange={(e) => setMethod(e.target.value as Method)}>
              {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </Select>
            <Input aria-label={`Reference for ${name}`} className="h-9 w-32" placeholder="Reference" value={reference} onChange={(e) => setReference(e.target.value)} />
            <Input aria-label={`Paid on for ${name}`} className="h-9 w-36" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
            <Button size="sm" type="submit" loading={paid.isPending}>Save</Button>
          </form>
        ) : null}
        {refunding ? <RefundForm invoice={i} name={name} onDone={() => { setRefunding(false); void qc.invalidateQueries({ queryKey: key }); }} /> : null}
        {i.refunds.length > 0 ? (
          <ul className="mt-1 grid gap-0.5 text-xs">
            {i.refunds.map((r) => <li key={r.at}>Refund {formatTaka("en", r.amount_paisa)} · {r.kind === "undelivered" ? "undelivered work" : "unused months"} · {r.reason}</li>)}
          </ul>
        ) : null}
        {paid.isError ? <QueryError error={paid.error} /> : null}
        {overdue.isError ? <QueryError error={overdue.error} /> : null}
      </td>
      <td className="px-4 py-3 text-right">
        {open && !paying ? (
          <>
            <Button size="sm" variant="ghost" aria-label={`Mark ${name} paid`} onClick={() => setPaying(true)}>Mark paid</Button>
            {i.status === "due" ? (
              <Button size="sm" variant="ghost" aria-label={`Mark ${name} overdue`} loading={overdue.isPending} onClick={() => overdue.mutate()}>Mark overdue</Button>
            ) : null}
          </>
        ) : null}
        {i.status === "paid" && !refunding && Math.max(i.refundable_unused_paisa, i.refundable_undelivered_paisa) > 0 ? (
          <Button size="sm" variant="ghost" aria-label={`Refund ${name}`} onClick={() => setRefunding(true)}>Refund</Button>
        ) : null}
      </td>
    </tr>
  );
}

/** Refund within the rules: unused prepaid months, or undelivered work. */
function RefundForm({ invoice: i, name, onDone }: { invoice: Invoice; name: string; onDone: () => void }) {
  const { api } = useBrandex();
  const superAdmin = useIsSuperAdmin();
  const [kind, setKind] = useState<RefundKind>(i.refundable_unused_paisa > 0 ? "unused_months" : "undelivered");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const cap = kind === "unused_months" ? i.refundable_unused_paisa : i.refundable_undelivered_paisa;
  const paisa = Math.round(Number(amount) * 100);
  const tooBig = paisa > cap;
  const needsRoot = paisa > REFUND_SUPER_ADMIN_ABOVE && !superAdmin;
  const refund = useMutation({
    mutationFn: async () => {
      await api.http.POST("/v1/admin/invoices/{invoice_id}/refund", {
        params: { path: { invoice_id: i.id } },
        body: { kind, amount_paisa: paisa, reason: reason.trim() },
      });
    },
    onSuccess: onDone,
  });
  return (
    <form className="mt-2 grid max-w-md gap-2 rounded-md border border-border p-3" onSubmit={(e) => { e.preventDefault(); refund.mutate(); }}>
      <Select aria-label={`Refund kind for ${name}`} value={kind} onChange={(e) => setKind(e.target.value as RefundKind)}>
        <option value="unused_months" disabled={i.refundable_unused_paisa === 0}>Unused months (up to {formatTaka("en", i.refundable_unused_paisa)})</option>
        <option value="undelivered" disabled={i.refundable_undelivered_paisa === 0}>Undelivered work (up to {formatTaka("en", i.refundable_undelivered_paisa)})</option>
      </Select>
      <Field label="Amount (৳)" error={tooBig ? `At most ${formatTaka("en", cap)}.` : needsRoot ? "Above ৳50,000 needs a Super admin." : fieldError(refund.error, "amount")}>
        <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))} />
      </Field>
      <Field label="Reason" hint="Kept in the audit log. Paid back to the original method within 10 working days." error={fieldError(refund.error, "reason")}>
        <Input value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} />
      </Field>
      {refund.isError && fieldError(refund.error, "amount", "reason") === undefined ? <QueryError error={refund.error} /> : null}
      <div className="flex gap-2">
        <Button size="sm" type="submit" disabled={paisa <= 0 || tooBig || needsRoot || reason.trim().length < 3} loading={refund.isPending}>Refund</Button>
        <Button size="sm" variant="ghost" type="button" onClick={onDone}>Cancel</Button>
      </div>
    </form>
  );
}
